import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { EMPTY_STRING, activeStatusMapping } from "src/components/constant/constant";
import HighLighter from "src/components/UI/highlighter/HighLighter";
import { ShortIdChip } from "src/components/UI/shortIdChip";
import { DateStack, EmptyCell, cellClasses } from "src/components/UI/transactionCells";
import StatusPill from "src/components/userComponent/profileHero/StatusPill";
import { AttributeCatalogType } from "src/types/commonTypes";
import { MasterCatalogRow } from "./masterCatalogSlice";
import styles from "./masterCatalog.module.scss";

interface ConfigParams {
  type?: AttributeCatalogType;
  searchText?: string;
  canEdit?: boolean;
  onEdit?: (_row: MasterCatalogRow) => void;
  onStatusChange?: (_row: MasterCatalogRow) => void;
}

const APPLIES_TO_LABELS: Record<string, string> = {
  ACCOUNTING_TRANSACTION: "Transactions"
};

const formatAppliesTo = (value: string) => APPLIES_TO_LABELS[value] ?? value;

const masterCatalogColumns = ({
  type,
  searchText = EMPTY_STRING,
  canEdit = false,
  onEdit,
  onStatusChange
}: ConfigParams = {}) => {
  const baseColumns = [
    {
      title: "ID",
      dataIndex: "displayId",
      key: "displayId",
      width: 110,
      render: (value: string) => (
        <ShortIdChip id={value ?? EMPTY_STRING} copiedLabel="ID Copied!" fallback="---" />
      )
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 160,
      render: (value: string) =>
        value ? <span className={styles.codeCell}>{value}</span> : <EmptyCell />
    },
    {
      title: type === "CUSTOM_ATTRIBUTE" ? "Name" : "Title",
      dataIndex: "title",
      key: "title",
      width: 240,
      render: (value: string) => {
        if (!value) return <EmptyCell />;

        return (
          <div className={cellClasses.tableMemberName}>
            <HighLighter searchText={searchText} text={value} />
          </div>
        );
      }
    }
  ];

  const customAttributeColumns = [
    {
      title: "Applies To",
      dataIndex: "appliesTo",
      key: "appliesTo",
      width: 180,
      render: (value: string[] | undefined) => {
        if (!value || value.length === 0) return <EmptyCell />;

        return (
          <div className={styles.appliesToCell}>
            {value.map((entry) => (
              <span key={entry} className={styles.appliesToChip}>
                {formatAppliesTo(entry)}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      title: "Value Type",
      dataIndex: "valueType",
      key: "valueType",
      width: 120,
      render: (value: string | undefined) =>
        value ? <span className={styles.valueTypeCell}>{value}</span> : <EmptyCell />
    },
    {
      title: "Options",
      dataIndex: "options",
      key: "options",
      width: 220,
      render: (value: string[] | undefined, record: MasterCatalogRow) => {
        if (record.valueType !== "ENUM") return <EmptyCell />;
        if (!value || value.length === 0) return <EmptyCell />;

        return <span className={styles.optionsCell}>{value.join(", ")}</span>;
      }
    }
  ];

  const tailColumns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: MasterCatalogRow["status"]) => (
        <StatusPill
          variant={activeStatusMapping[status] ? "active" : "inactive"}
          label={activeStatusMapping[status] ? "Active" : "Inactive"}
        />
      )
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 160,
      render: (value: string | undefined, record: MasterCatalogRow) => (
        <DateStack value={value ?? record.createdAt} />
      )
    },
    {
      title: EMPTY_STRING,
      dataIndex: EMPTY_STRING,
      key: "operations",
      width: 96,
      render: (_value: unknown, record: MasterCatalogRow) => {
        if (!canEdit) return null;
        const isActive = activeStatusMapping[record.status];
        const statusLabel = isActive ? "Deactivate" : "Reactivate";

        return (
          <div className={styles.rowActions}>
            <Tooltip title="Edit" placement="top" arrow>
              <IconButton
                size="small"
                className={styles.actionBtn}
                onClick={() => onEdit?.(record)}
                aria-label={`Edit ${record.code}`}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={statusLabel} placement="top" arrow>
              <IconButton
                size="small"
                className={styles.actionBtn}
                onClick={() => onStatusChange?.(record)}
                aria-label={`${statusLabel} ${record.code}`}
              >
                {isActive ? <BlockOutlinedIcon fontSize="small" /> : <RestartAltOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </div>
        );
      }
    }
  ];

  if (type === "CUSTOM_ATTRIBUTE") {
    return [...baseColumns, ...customAttributeColumns, ...tailColumns];
  }

  return [...baseColumns, ...tailColumns];
};

export default masterCatalogColumns;
